import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import {
  ArrowLeft,
  ArrowRightLeft,
  AlertTriangle,
  BadgeCheck,
  Bitcoin,
  Clock3,
  PackageCheck,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Star,
  TrendingUp,
  Truck,
} from "lucide-react"
import { Card, CardContent } from "../ui/card"
import { Button } from "../ui/button"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../config/featureFlags"

const API_AUDIENCE = process.env.REACT_APP_API_AUDIENCE

const formatDate = (value) => {
  if (!value) return "Date unavailable"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Date unavailable"
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date)
}

const formatBtc = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return "0.00000000 BTC"
  return `${number.toFixed(8)} BTC`
}

const normalizeId = (value) => String(value || "").trim()
const normalizeUserId = (value) => normalizeId(value).replace(/[|-]/g, "")

const getAssetTitle = (asset) => asset?.productName || asset?.assetName || `Asset #${asset?.id || "unknown"}`

const shortId = (value) => {
  const text = normalizeId(value)
  if (!text) return "Not recorded"
  if (text.length <= 18) return text
  return `${text.slice(0, 8)}...${text.slice(-8)}`
}

const getBtcProofAmount = (transaction, intentByTransactionId) => {
  const intent = intentByTransactionId.get(Number(transaction.id))
  const intentAmount = Number(intent?.amount)
  if (Number.isFinite(intentAmount) && intentAmount > 0) {
    return intentAmount
  }

  const transactionAmount = Number(transaction.salePrice)
  return Number.isFinite(transactionAmount) ? transactionAmount : 0
}

const statusTone = (status) => {
  const normalized = String(status || "").toLowerCase()
  if (["accepted", "completed", "sent", "received"].includes(normalized)) {
    return "border-emerald-300/35 bg-emerald-400/10 text-emerald-100"
  }
  if (["pending", "open"].includes(normalized)) {
    return "border-amber-300/35 bg-amber-400/10 text-amber-100"
  }
  if (["rejected", "cancelled", "failed", "disputed"].includes(normalized)) {
    return "border-rose-300/35 bg-rose-400/10 text-rose-100"
  }
  return "border-sky-300/35 bg-sky-400/10 text-sky-100"
}

const tradeItemStage = (item) => {
  if (item?.recievedOn) return "Received"
  if (item?.shippedOn) return "Shipped"
  return "Needs shipment"
}

function FulfillmentControls({ entry, actionState, onShipItem, onReceiveItem, onDisputeTrade }) {
  const [forms, setForms] = useState({})

  if (entry.type !== "trade" || !entry.tradeItems?.length) {
    return null
  }

  const accepted = String(entry.status || "").toLowerCase() === "accepted"
  const updateForm = (itemId, field, value) => {
    setForms((current) => ({
      ...current,
      [itemId]: {
        ...(current[itemId] || {}),
        [field]: value,
      },
    }))
  }

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-slate-900/50 p-4">
      <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-base font-bold text-white">Fulfillment and protection</h3>
          <p className="text-sm text-slate-400">Record shipment, confirm receipt, or open a dispute from the same trade timeline.</p>
        </div>
        {accepted && (
          <button
            type="button"
            onClick={() => onDisputeTrade(entry.tradeId)}
            disabled={actionState.busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-300/35 bg-rose-500/10 px-3 py-2 text-sm font-bold text-rose-100 transition hover:bg-rose-500/20 disabled:opacity-60"
          >
            <AlertTriangle className="h-4 w-4" />
            Dispute trade
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3">
        {entry.tradeItems.map((item) => {
          const itemId = item.id || item.assetId
          const isSender = normalizeUserId(item.senderId) === entry.currentUserId
          const isRecipient = normalizeUserId(item.recipientId) === entry.currentUserId
          const canShip = accepted && isSender && !item.shippedOn && !item.recievedOn
          const canReceive = accepted && isRecipient && !item.recievedOn
          const form = forms[itemId] || {}

          return (
            <div key={`${entry.tradeId}-${itemId}`} className="rounded-xl border border-white/10 bg-slate-950/60 p-3">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white">{item.assetTitle || `Asset #${item.assetId}`}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.12em] text-slate-500">
                    {isSender ? "You send" : isRecipient ? "You receive" : "Counterparty item"}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-300">
                    <span className={`rounded-full border px-2 py-1 ${statusTone(tradeItemStage(item))}`}>{tradeItemStage(item)}</span>
                    {item.carrier && <span className="rounded-full border border-white/10 bg-slate-900 px-2 py-1">{item.carrier}</span>}
                    {item.trackingId && <span className="rounded-full border border-white/10 bg-slate-900 px-2 py-1">{item.trackingId}</span>}
                  </div>
                </div>

                {canShip && (
                  <div className="grid w-full gap-2 md:max-w-md md:grid-cols-[1fr_1fr_auto]">
                    <input
                      value={form.carrier || ""}
                      onChange={(event) => updateForm(itemId, "carrier", event.target.value)}
                      placeholder="Carrier"
                      aria-label={`Carrier for ${item.assetTitle || `asset ${item.assetId}`}`}
                      className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-200/60"
                    />
                    <input
                      value={form.trackingId || ""}
                      onChange={(event) => updateForm(itemId, "trackingId", event.target.value)}
                      placeholder="Tracking or pickup code"
                      aria-label={`Tracking for ${item.assetTitle || `asset ${item.assetId}`}`}
                      className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-200/60"
                    />
                    <button
                      type="button"
                      onClick={() => onShipItem(entry.tradeId, item.id, form)}
                      disabled={actionState.busy}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-400 px-3 py-2 text-sm font-bold text-slate-950 transition hover:bg-cyan-300 disabled:opacity-60"
                    >
                      <Truck className="h-4 w-4" />
                      Mark shipped
                    </button>
                  </div>
                )}

                {canReceive && (
                  <button
                    type="button"
                    onClick={() => onReceiveItem(entry.tradeId, item.id)}
                    disabled={actionState.busy}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-400 px-3 py-2 text-sm font-bold text-slate-950 transition hover:bg-emerald-300 disabled:opacity-60"
                  >
                    <PackageCheck className="h-4 w-4" />
                    Confirm received
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TransactionFulfillmentControls({ entry, actionState, onShipTransaction, onReceiveTransaction, onDisputeTransaction }) {
  const [form, setForm] = useState({})

  if (entry.type !== "transaction") {
    return null
  }

  const canShip = entry.isSeller && !entry.shippedOn && !entry.recievedOn && !entry.disputedOn
  const canReceive = entry.isBuyer && !!entry.shippedOn && !entry.recievedOn && !entry.disputedOn
  const canDispute = (entry.isBuyer || entry.isSeller) && !entry.recievedOn && !entry.disputedOn

  if (!canShip && !canReceive && !canDispute) {
    return null
  }

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-slate-900/50 p-4">
      <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-base font-bold text-white">Delivery and protection</h3>
          <p className="text-sm text-slate-400">Record shipment, confirm receipt, or open a dispute for this BTC marketplace purchase.</p>
        </div>
        {canDispute && (
          <button
            type="button"
            onClick={() => onDisputeTransaction(entry.transactionId)}
            disabled={actionState.busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-300/35 bg-rose-500/10 px-3 py-2 text-sm font-bold text-rose-100 transition hover:bg-rose-500/20 disabled:opacity-60"
          >
            <AlertTriangle className="h-4 w-4" />
            Dispute purchase
          </button>
        )}
      </div>

      {canShip && (
        <div className="grid w-full gap-2 md:grid-cols-[1fr_1fr_auto]">
          <input
            value={form.carrier || ""}
            onChange={(event) => setForm((current) => ({ ...current, carrier: event.target.value }))}
            placeholder="Carrier"
            aria-label={`Carrier for ${entry.assetTitle}`}
            className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-200/60"
          />
          <input
            value={form.trackingId || ""}
            onChange={(event) => setForm((current) => ({ ...current, trackingId: event.target.value }))}
            placeholder="Tracking or pickup code"
            aria-label={`Tracking for ${entry.assetTitle}`}
            className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-200/60"
          />
          <button
            type="button"
            onClick={() => onShipTransaction(entry.transactionId, form)}
            disabled={actionState.busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-400 px-3 py-2 text-sm font-bold text-slate-950 transition hover:bg-cyan-300 disabled:opacity-60"
          >
            <Truck className="h-4 w-4" />
            Mark shipped
          </button>
        </div>
      )}

      {canReceive && (
        <button
          type="button"
          onClick={() => onReceiveTransaction(entry.transactionId)}
          disabled={actionState.busy}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-400 px-3 py-2 text-sm font-bold text-slate-950 transition hover:bg-emerald-300 disabled:opacity-60"
        >
          <PackageCheck className="h-4 w-4" />
          Confirm received
        </button>
      )}
    </div>
  )
}

function ReviewControls({ entry, actionState, onSubmitReview }) {
  const [rating, setRating] = useState(5)
  const [comment, setComment] = useState("")

  if (!entry.review?.available) {
    return null
  }

  const submitReview = () => {
    onSubmitReview(entry.review, {
      rating: Number(rating),
      comment,
    })
  }

  return (
    <div className="mt-4 rounded-2xl border border-amber-200/20 bg-amber-300/10 p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-white">
            <Star className="h-4 w-4 text-amber-200" />
            Leave marketplace feedback
          </h3>
          <p className="mt-1 text-sm leading-6 text-slate-300">
            Rate the completed exchange so future buyers and sellers can trade with more confidence.
          </p>
        </div>
        <div className="grid w-full gap-2 md:max-w-md md:grid-cols-[110px_1fr_auto]">
          <select
            value={rating}
            onChange={(event) => setRating(event.target.value)}
            aria-label={`Rating for ${entry.review.counterpartyLabel}`}
            className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm font-bold text-white outline-none focus:border-amber-200/70"
          >
            {[5, 4, 3, 2, 1].map((value) => (
              <option key={value} value={value}>{value} stars</option>
            ))}
          </select>
          <input
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Optional public note"
            aria-label={`Feedback note for ${entry.review.counterpartyLabel}`}
            maxLength={500}
            className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-amber-200/70"
          />
          <button
            type="button"
            onClick={submitReview}
            disabled={actionState.busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-300 px-3 py-2 text-sm font-bold text-slate-950 transition hover:bg-amber-200 disabled:opacity-60"
          >
            <Star className="h-4 w-4" />
            Save feedback
          </button>
        </div>
      </div>
    </div>
  )
}

function HistoryMetric({ icon, label, value, helper }) {
  return (
    <Card className="border-white/10 bg-slate-950/55 shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
      <CardContent className="flex min-h-[118px] gap-4 p-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-slate-900/80 text-cyan-200">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-300">{label}</p>
          <p className="mt-1 text-2xl font-bold text-white">{value}</p>
          {helper && <p className="mt-1 text-sm text-slate-400">{helper}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

function AssetChip({ asset, fallback }) {
  const title = asset ? getAssetTitle(asset) : fallback
  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-white/10 bg-slate-900/70 py-1 pl-1 pr-3 text-sm text-slate-200">
      {asset?.imageURL ? (
        <img src={asset.imageURL} alt="" className="h-7 w-7 rounded-full object-cover" />
      ) : (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-cyan-200">
          <PackageCheck className="h-4 w-4" />
        </span>
      )}
      <span className="min-w-0 truncate">{title}</span>
    </span>
  )
}

function TimelineEntry({
  entry,
  actionState,
  onShipItem,
  onReceiveItem,
  onDisputeTrade,
  onShipTransaction,
  onReceiveTransaction,
  onDisputeTransaction,
  onSubmitReview,
}) {
  const isTrade = entry.type === "trade"
  const Icon = isTrade ? ArrowRightLeft : Bitcoin

  return (
    <article className="relative pl-10">
      <div className="absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full border border-cyan-200/30 bg-slate-950 text-cyan-200 shadow-[0_0_24px_rgba(34,211,238,0.2)]">
        <Icon className="h-4 w-4" />
      </div>
      <Card className="border-white/10 bg-slate-950/62 shadow-[0_18px_65px_rgba(0,0,0,0.38)] backdrop-blur">
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-xs font-bold uppercase tracking-[0.12em] ${statusTone(entry.status)}`}>
                  {entry.status}
                </span>
                <span className="text-sm text-slate-400">{formatDate(entry.createdOn)}</span>
              </div>
              <h2 className="text-xl font-bold text-white">{entry.title}</h2>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-300">{entry.description}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-slate-900/72 px-3 py-2 text-right">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{entry.amountLabel}</p>
              <p className="text-lg font-bold text-white">{entry.amount}</p>
            </div>
          </div>

          {entry.assets?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {entry.assets.map((assetEntry) => (
                <AssetChip key={assetEntry.key} asset={assetEntry.asset} fallback={assetEntry.fallback} />
              ))}
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-3 border-t border-white/10 pt-4 text-sm text-slate-300 md:grid-cols-3">
            {entry.facts.map((fact) => (
              <div key={fact.label} className="min-w-0 rounded-lg border border-white/10 bg-slate-900/45 p-3">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{fact.label}</p>
                <p className="mt-1 truncate font-medium text-slate-100" title={fact.value}>{fact.value}</p>
              </div>
            ))}
          </div>

          <FulfillmentControls
            entry={entry}
            actionState={actionState}
            onShipItem={onShipItem}
            onReceiveItem={onReceiveItem}
            onDisputeTrade={onDisputeTrade}
          />
          <TransactionFulfillmentControls
            entry={entry}
            actionState={actionState}
            onShipTransaction={onShipTransaction}
            onReceiveTransaction={onReceiveTransaction}
            onDisputeTransaction={onDisputeTransaction}
          />
          <ReviewControls
            entry={entry}
            actionState={actionState}
            onSubmitReview={onSubmitReview}
          />
        </CardContent>
      </Card>
    </article>
  )
}

function EmptyHistory() {
  return (
    <Card className="border-white/10 bg-slate-950/60 text-center shadow-[0_20px_70px_rgba(0,0,0,0.35)]">
      <CardContent className="p-8">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-200/25 bg-cyan-400/10 text-cyan-200">
          <Sparkles className="h-7 w-7" />
        </div>
        <h2 className="text-2xl font-bold text-white">No trading story yet</h2>
        <p className="mx-auto mt-2 max-w-xl text-slate-300">
          Purchases, sales, and trade offers will appear here as a chronological record once you start using the marketplace.
        </p>
        <Button asChild className="mt-5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
          <Link to="/marketplace">Explore marketplace</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

export default function TradingHistoryPage() {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0()
  const apiUrl = process.env.REACT_APP_API_URL
  const marketplaceTradesEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceTrades)
  const bitcoinTransactionsEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience)
  const userId = user?.sub
  const [trades, setTrades] = useState([])
  const [transactions, setTransactions] = useState([])
  const [marketplacePaymentIntents, setMarketplacePaymentIntents] = useState([])
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [actionState, setActionState] = useState({ busy: false, message: "", error: "" })
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    let isMounted = true

    const fetchHistory = async () => {
      if (!isAuthenticated || !userId || !apiUrl) {
        return
      }

      setLoading(true)
      setError("")

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: API_AUDIENCE },
        })
        const headers = { Authorization: `Bearer ${token}` }
        const requests = [
          marketplaceTradesEnabled
            ? fetch(`${apiUrl}/api/trades`, { headers }).then((response) => response.ok ? response.json() : [] )
            : Promise.resolve([]),
          (marketplaceTradesEnabled || bitcoinTransactionsEnabled)
            ? fetch(`${apiUrl}/api/transactions`, { headers }).then((response) => response.ok ? response.json() : [] )
            : Promise.resolve([]),
          fetch(`${apiUrl}/api/assets/user/${encodeURIComponent(userId)}`, { headers }).then((response) => response.ok ? response.json() : []),
          bitcoinTransactionsEnabled
            ? fetch(`${apiUrl}/api/Marketplace/btc-payment-intents?pageSize=200`, { headers }).then((response) => response.ok ? response.json() : [] )
            : Promise.resolve([]),
        ]

        const [tradeData, transactionData, assetData, paymentIntentData] = await Promise.all(requests)
        if (!isMounted) return
        setTrades(Array.isArray(tradeData) ? tradeData : [])
        setTransactions(Array.isArray(transactionData) ? transactionData : [])
        setAssets(Array.isArray(assetData) ? assetData : [])
        setMarketplacePaymentIntents(Array.isArray(paymentIntentData) ? paymentIntentData : [])
      } catch (err) {
        console.error("Error fetching trading history:", err)
        if (isMounted) {
          setError("We could not load your trading history. Please try again.")
          setTrades([])
          setTransactions([])
          setAssets([])
          setMarketplacePaymentIntents([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchHistory()

    return () => {
      isMounted = false
    }
  }, [apiUrl, bitcoinTransactionsEnabled, getAccessTokenSilently, isAuthenticated, marketplaceTradesEnabled, reloadToken, userId])

  const runTradeAction = async (path, options = {}, successMessage = "Trade updated.") => {
    setActionState({ busy: true, message: "", error: "" })
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: API_AUDIENCE },
      })
      const response = await fetch(`${apiUrl}${path}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          ...(options.body ? { "Content-Type": "application/json" } : {}),
        },
        ...options,
      })
      if (!response.ok) {
        throw new Error(`Trade update failed (${response.status})`)
      }
      setActionState({ busy: false, message: successMessage, error: "" })
      setReloadToken((value) => value + 1)
    } catch (err) {
      console.error("Trade action failed:", err)
      setActionState({ busy: false, message: "", error: "We could not update this trade. Please retry." })
    }
  }

  const handleShipItem = (tradeId, itemId, form) => runTradeAction(
    `/api/trades/${tradeId}/items/${itemId}/ship`,
    { body: JSON.stringify({ carrier: form.carrier || "", trackingId: form.trackingId || "" }) },
    "Shipment details recorded.",
  )

  const handleReceiveItem = (tradeId, itemId) => runTradeAction(
    `/api/trades/${tradeId}/items/${itemId}/receive`,
    {},
    "Receipt confirmed.",
  )

  const handleDisputeTrade = (tradeId) => runTradeAction(
    `/api/trades/${tradeId}/dispute`,
    {},
    "Trade dispute opened.",
  )

  const handleShipTransaction = (transactionId, form) => runTradeAction(
    `/api/transactions/${transactionId}/ship`,
    { body: JSON.stringify({ carrier: form.carrier || "", trackingId: form.trackingId || "" }) },
    "Shipment details recorded.",
  )

  const handleReceiveTransaction = (transactionId) => runTradeAction(
    `/api/transactions/${transactionId}/receive`,
    {},
    "Receipt confirmed.",
  )

  const handleDisputeTransaction = (transactionId) => runTradeAction(
    `/api/transactions/${transactionId}/dispute`,
    {},
    "Marketplace dispute opened.",
  )

  const handleSubmitReview = (review, values) => runTradeAction(
    "/api/MarketplaceReviews",
    {
      method: "POST",
      body: JSON.stringify({
        revieweeUserId: review.revieweeUserId,
        transactionId: review.transactionId,
        tradeId: review.tradeId,
        rating: values.rating,
        comment: values.comment,
      }),
    },
    "Marketplace feedback saved.",
  )

  const assetById = useMemo(() => {
    const map = new Map()
    assets.forEach((asset) => map.set(Number(asset.id), asset))
    return map
  }, [assets])

  const intentByTransactionId = useMemo(() => {
    const map = new Map()
    marketplacePaymentIntents.forEach((intent) => {
      if (intent?.marketplaceTransactionId) {
        map.set(Number(intent.marketplaceTransactionId), intent)
      }
    })
    return map
  }, [marketplacePaymentIntents])

  const timeline = useMemo(() => {
    const userId = normalizeUserId(user?.sub)
    const tradeEntries = trades.map((trade) => {
      const sentByUser = normalizeUserId(trade.senderId) === userId
      const items = Array.isArray(trade.tradeItems) ? trade.tradeItems : []
      const enrichedItems = items.map((item) => {
        const asset = assetById.get(Number(item.assetId))
        return {
          ...item,
          assetTitle: asset ? getAssetTitle(asset) : `Asset #${item.assetId}`,
        }
      })
      const assetsInTrade = items.map((item) => ({
        key: `trade-${trade.id}-${item.id || item.assetId}`,
        asset: assetById.get(Number(item.assetId)),
        fallback: `Asset #${item.assetId}`,
      }))
      const outgoingCount = items.filter((item) => normalizeUserId(item.senderId) === userId).length
      const incomingCount = items.filter((item) => normalizeUserId(item.recipientId) === userId).length
      const activeItems = enrichedItems.filter((item) => item.isDeleted !== true)
      const tradeCompleted = String(trade.status || "").toLowerCase() === "completed" ||
        (activeItems.length > 0 && activeItems.every((item) => item.recievedOn))
      const revieweeUserId = sentByUser ? trade.recipientId : trade.senderId

      return {
        id: `trade-${trade.id}`,
        type: "trade",
        status: trade.status || "Pending",
        createdOn: trade.createdOn,
        title: sentByUser ? "Trade offer sent" : "Trade offer received",
        description: sentByUser
          ? "You proposed an item-for-item exchange. Track whether it was accepted, rejected, cancelled, or still waiting."
          : "Someone made you a trade offer. This entry shows the status and the items attached to the offer.",
        amountLabel: "Items moving",
        amount: `${outgoingCount} out / ${incomingCount} in`,
        assets: assetsInTrade,
        tradeId: trade.id,
        tradeItems: enrichedItems,
        currentUserId: userId,
        review: {
          available: tradeCompleted && !!revieweeUserId,
          revieweeUserId,
          tradeId: trade.id,
          counterpartyLabel: shortId(revieweeUserId),
        },
        facts: [
          { label: "Trade ID", value: String(trade.id) },
          { label: "Counterparty", value: shortId(sentByUser ? trade.recipientId : trade.senderId) },
          { label: "Confirmation", value: trade.transactionDetailsConfirmed ? "Confirmed" : "Not confirmed" },
          { label: "Protection", value: String(trade.status || "").toLowerCase() === "accepted" ? "Shipment, receipt, and dispute controls available" : "Awaiting accepted trade" },
        ],
      }
    })

    const transactionEntries = transactions.map((transaction) => {
      const boughtByUser = normalizeUserId(transaction.buyer) === userId
      const soldByUser = normalizeUserId(transaction.seller) === userId
      const asset = assetById.get(Number(transaction.assetId))
      const assetTitle = asset ? getAssetTitle(asset) : `Asset #${transaction.assetId}`
      const btcIntent = intentByTransactionId.get(Number(transaction.id))
      const btcAmount = getBtcProofAmount(transaction, intentByTransactionId)
      const btcTransactionId = btcIntent?.transactionId || transaction.btcTransactionId
      const status = transaction.disputedOn ? "Disputed" : transaction.recievedOn ? "Received" : transaction.shippedOn ? "Sent" : "Pending"
      const revieweeUserId = boughtByUser ? transaction.seller : transaction.buyer

      return {
        id: `transaction-${transaction.id}`,
        type: "transaction",
        status,
        createdOn: transaction.createdOn,
        title: boughtByUser ? "BTC purchase" : "BTC sale",
        description: boughtByUser
          ? `You bought ${assetTitle} through the marketplace.`
          : `You sold ${assetTitle} through the marketplace.`,
        amountLabel: boughtByUser ? "Spent" : "Received",
        amount: formatBtc(btcAmount),
        assets: [{
          key: `transaction-${transaction.id}-${transaction.assetId}`,
          asset,
          fallback: `Asset #${transaction.assetId}`,
        }],
        review: {
          available: !!transaction.recievedOn && !!revieweeUserId,
          revieweeUserId,
          transactionId: transaction.id,
          counterpartyLabel: shortId(revieweeUserId),
        },
        transactionId: transaction.id,
        assetTitle,
        isBuyer: boughtByUser,
        isSeller: soldByUser,
        shippedOn: transaction.shippedOn,
        recievedOn: transaction.recievedOn,
        disputedOn: transaction.disputedOn,
        facts: [
          { label: "BTC txid", value: shortId(btcTransactionId) },
          btcIntent?.status ? { label: "BTC status", value: btcIntent.status } : null,
          { label: "Counterparty", value: shortId(boughtByUser ? transaction.seller : transaction.buyer) },
          { label: "Shipping", value: transaction.trackingId ? `${transaction.carrier || "Carrier"} ${transaction.trackingId}` : "Not recorded" },
          transaction.disputedOn ? { label: "Protection", value: "Dispute opened" } : null,
        ].filter(Boolean),
      }
    })

    return [...tradeEntries, ...transactionEntries].sort((a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0))
  }, [assetById, intentByTransactionId, trades, transactions, user])

  const stats = useMemo(() => {
    const userId = normalizeUserId(user?.sub)
    const acceptedTrades = trades.filter((trade) => String(trade.status || "").toLowerCase() === "accepted").length
    const pendingTrades = trades.filter((trade) => String(trade.status || "").toLowerCase() === "pending").length
    const btcSpent = transactions
      .filter((transaction) => normalizeUserId(transaction.buyer) === userId)
      .reduce((sum, transaction) => sum + getBtcProofAmount(transaction, intentByTransactionId), 0)
    const btcReceived = transactions
      .filter((transaction) => normalizeUserId(transaction.seller) === userId)
      .reduce((sum, transaction) => sum + getBtcProofAmount(transaction, intentByTransactionId), 0)

    return {
      totalEvents: timeline.length,
      acceptedTrades,
      pendingTrades,
      btcSpent,
      btcReceived,
    }
  }, [intentByTransactionId, timeline.length, trades, transactions, user])

  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-8 text-slate-100">
        <div className="mx-auto max-w-6xl rounded-2xl border border-white/10 bg-slate-900/70 p-6">Loading trading history...</div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return <div className="min-h-screen bg-slate-950 p-6 text-slate-100">Not authenticated</div>
  }

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_16%_0%,rgba(34,211,238,0.14),transparent_30%),radial-gradient(circle_at_90%_10%,rgba(250,204,21,0.12),transparent_34%),linear-gradient(180deg,#070b16_0%,#111827_52%,#070b16_100%)] px-4 pb-12 pt-6 text-slate-100">
      <div className="mx-auto w-full max-w-7xl">
        <Link to="/dashboard" className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 transition hover:text-cyan-100 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <section className="mb-6 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/55 shadow-[0_25px_90px_rgba(0,0,0,0.45)]">
          <div className="relative p-6 md:p-8">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.16),transparent_34%),radial-gradient(circle_at_84%_0%,rgba(245,158,11,0.18),transparent_36%)]" />
            <div className="relative max-w-4xl">
              <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-200/30 bg-amber-400/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-amber-100">
                <TrendingUp className="h-4 w-4" /> Trading ledger
              </p>
              <h1 className="text-3xl font-bold md:text-5xl">Your marketplace history</h1>
              <p className="mt-3 text-base leading-7 text-slate-300 md:text-lg">
                Follow every purchase, sale, and trade offer as a timeline. This is the record of how your inventory changes hands over time.
              </p>
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-6 rounded-xl border border-rose-300/30 bg-rose-500/10 px-4 py-3 text-rose-100">
            {error}
          </div>
        )}
        {actionState.error && (
          <div className="mb-6 rounded-xl border border-rose-300/30 bg-rose-500/10 px-4 py-3 text-rose-100" role="alert">
            {actionState.error}
          </div>
        )}
        {actionState.message && (
          <div className="mb-6 rounded-xl border border-emerald-300/30 bg-emerald-500/10 px-4 py-3 text-emerald-100" role="status">
            {actionState.message}
          </div>
        )}

        <section className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <HistoryMetric icon={<ReceiptText className="h-5 w-5" />} label="Timeline events" value={stats.totalEvents} helper="Trades plus BTC marketplace transactions" />
          <HistoryMetric icon={<BadgeCheck className="h-5 w-5" />} label="Accepted trades" value={stats.acceptedTrades} helper={`${stats.pendingTrades} trade offers still pending`} />
          <HistoryMetric icon={<Bitcoin className="h-5 w-5" />} label="BTC spent" value={formatBtc(stats.btcSpent)} helper="Marketplace purchases" />
          <HistoryMetric icon={<ShieldCheck className="h-5 w-5" />} label="BTC received" value={formatBtc(stats.btcReceived)} helper="Marketplace sales" />
        </section>

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="relative space-y-5 before:absolute before:left-4 before:top-3 before:h-[calc(100%-1.5rem)] before:w-px before:bg-cyan-200/20">
            {timeline.length === 0 ? (
              <EmptyHistory />
            ) : (
              timeline.map((entry) => (
                <TimelineEntry
                  key={entry.id}
                  entry={entry}
                  actionState={actionState}
                  onShipItem={handleShipItem}
                  onReceiveItem={handleReceiveItem}
                  onDisputeTrade={handleDisputeTrade}
                  onShipTransaction={handleShipTransaction}
                  onReceiveTransaction={handleReceiveTransaction}
                  onDisputeTransaction={handleDisputeTransaction}
                  onSubmitReview={handleSubmitReview}
                />
              ))
            )}
          </div>

          <aside className="space-y-4">
            <Card className="border-white/10 bg-slate-950/62 shadow-[0_18px_65px_rgba(0,0,0,0.35)]">
              <CardContent className="p-5">
                <h2 className="text-xl font-bold text-white">Trade climb</h2>
                <p className="mt-2 text-sm leading-6 text-slate-300">
                  Use the timeline to watch the path from small inventory swaps to larger wins. Accepted trades and completed BTC purchases become the milestones.
                </p>
                <div className="mt-4 rounded-xl border border-cyan-200/20 bg-cyan-400/10 p-4">
                  <p className="text-sm font-semibold text-cyan-100">Current momentum</p>
                  <p className="mt-1 text-2xl font-bold text-white">{stats.acceptedTrades + transactions.length}</p>
                  <p className="text-sm text-slate-300">completed trade or transaction events</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-slate-950/62 shadow-[0_18px_65px_rgba(0,0,0,0.35)]">
              <CardContent className="p-5">
                <h2 className="text-xl font-bold text-white">Useful checks</h2>
                <ul className="mt-3 space-y-3 text-sm text-slate-300">
                  <li className="flex gap-2"><Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" /> Pending trades still need a response.</li>
                  <li className="flex gap-2"><Bitcoin className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" /> BTC entries show transaction IDs when the broadcast was recorded.</li>
                  <li className="flex gap-2"><PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" /> Shipping details appear when a carrier or tracking ID exists.</li>
                </ul>
              </CardContent>
            </Card>
          </aside>
        </section>
      </div>
    </main>
  )
}
